<rfid-bulk-upload>
  <div class="rfid-bulk-upload">
    <div class="alert alert-info">
      <i class="bi bi-info-circle me-2"></i>
      Puedes cargar múltiples tarjetas desde un archivo CSV o Excel.
      <a href="#" onclick={downloadTemplate} class="alert-link">Descarga la plantilla aquí</a>.
      El formato recomendado es: <strong>fecha, numeroserie, numeroimpreso, nombreusuario, estado</strong>.
    </div>

    <div if={props.isAdmin} class="mb-3">
      <label class="form-label">Usuario propietario *</label>
      <select class="form-select" onchange={handleUserChange} value={state.targetUserId}>
        <option value="">Selecciona un usuario</option>
        <option each={user in availableUsers} value={user._id} key={user._id}>
          {user.username} ({user.email}) · {user._id}
        </option>
      </select>
      <div class="form-text">Las tarjetas cargadas se asignarán al usuario seleccionado.</div>
    </div>

    <!-- File upload -->
    <div class="mb-3">
      <label class="form-label">Seleccionar Archivo</label>
      <input
        type="file"
        class="form-control"
        accept=".csv,.xlsx,.xls"
        onchange={handleFileSelect}
      />
      <div class="form-text">
        Formatos soportados: CSV, Excel (.xlsx, .xls). Tamaño máximo: 5MB
      </div>
    </div>

    <!-- File info -->
    <div if={state.selectedFile} class="card mb-3">
      <div class="card-body">
        <h6 class="card-title">Archivo Seleccionado</h6>
        <p class="mb-1">
          <strong>Nombre:</strong> {state.selectedFile.name}
        </p>
        <p class="mb-0">
          <strong>Tamaño:</strong> {formatFileSize(state.selectedFile.size)}
        </p>
      </div>
    </div>

    <!-- Upload button -->
    <div class="d-grid gap-2">
      <button
        class="btn btn-primary"
        onclick={handleUpload}
        disabled={!state.selectedFile || state.uploading}
      >
        <span if={state.uploading}>
          <span class="spinner-border spinner-border-sm me-2"></span>
          Procesando...
        </span>
        <span if={!state.uploading}>
          <i class="bi bi-cloud-upload me-2"></i>
          Subir y Procesar
        </span>
      </button>
    </div>

    <!-- Upload result -->
    <div if={state.result} class="mt-4">
      <div class="alert alert-success">
        <h6 class="alert-heading">
          <i class="bi bi-check-circle me-2"></i>
          Procesamiento Completado
        </h6>
        <p class="mb-0">
          <strong>{state.result.success}</strong> tarjeta(s) añadida(s) correctamente.
        </p>
      </div>

      <!-- Duplicados -->
      <div if={state.result.duplicates.length > 0} class="alert alert-warning">
        <h6 class="alert-heading">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {state.result.duplicates.length} Tarjeta(s) Duplicada(s)
        </h6>
        <p class="mb-2">Las siguientes tarjetas ya existen en el sistema:</p>
        <ul class="mb-0 small">
          <li each={dup in state.result.duplicates} key={dup.row}>
            Fila {dup.row}: <code>{dup.cardNumber}</code>
          </li>
        </ul>
      </div>

      <!-- Errores -->
      <div if={state.result.errors.length > 0} class="alert alert-danger">
        <h6 class="alert-heading">
          <i class="bi bi-x-circle me-2"></i>
          {state.result.errors.length} Error(es) Encontrado(s)
        </h6>
        <div class="accordion accordion-flush" id="errorsAccordion">
          <div each={error in state.result.errors} key={error.row} class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={'#error-' + error.row}
              >
                Fila {error.row}: {error.error}
              </button>
            </h2>
            <div id={'error-' + error.row} class="accordion-collapse collapse" data-bs-parent="#errorsAccordion">
              <div class="accordion-body">
                <pre class="mb-0"><code>{JSON.stringify(error.data, null, 2)}</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reset button -->
      <div class="d-grid gap-2 mt-3">
        <button class="btn btn-outline-secondary" onclick={reset}>
          <i class="bi bi-arrow-counterclockwise me-2"></i>
          Cargar Otro Archivo
        </button>
      </div>
    </div>
  </div>

  <script>
    import { RfidCardService } from '../services/api';
    import { ToastService } from '../services/toast-service';

    export default {
      get availableUsers() {
        return Array.isArray(this.props.users) ? this.props.users : [];
      },

      onBeforeMount(props) {
        console.log('RfidBulkUpload mounted with props:', props);
        this.state = {
          selectedFile: null,
          uploading: false,
          result: null,
          targetUserId: props.defaultUserId || props.userId || ''
        };
      },

      onUpdated(props) {
        if (!this.state.targetUserId && (props.defaultUserId || props.userId)) {
          this.update({ targetUserId: props.defaultUserId || props.userId });
        }
      },

      handleUserChange(e) {
        this.update({ targetUserId: e.target.value });
      },

      handleFileSelect(e) {
        const file = e.target.files[0];

        if (!file) {
          this.update({ selectedFile: null });
          return;
        }

        // Validar tamaño (max 5MB)
        const MAX_SIZE = 5 * 1024 * 1024;
        if (file.size > MAX_SIZE) {
          ToastService.error('El archivo es demasiado grande. Máximo 5MB');
          e.target.value = '';
          return;
        }

        // Validar extensión
        const validExtensions = ['.csv', '.xlsx', '.xls'];
        const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!validExtensions.includes(extension)) {
          ToastService.error('Formato de archivo no válido. Use CSV o Excel');
          e.target.value = '';
          return;
        }

        this.update({ selectedFile: file, result: null });
      },

      async handleUpload() {
        const targetUserId = this.props.isAdmin
          ? this.state.targetUserId
          : this.props.userId;

        if (!targetUserId) {
          ToastService.error('Selecciona el usuario propietario de las tarjetas');
          return;
        }

        if (!this.state.selectedFile) {
          ToastService.error('Por favor selecciona un archivo');
          return;
        }

        try {
          this.update({ uploading: true });

          const result = await RfidCardService.bulkUpload(
            targetUserId,
            this.state.selectedFile
          );

          this.update({ result, uploading: false });

          if (result.success > 0) {
            ToastService.success(`${result.success} tarjeta(s) añadida(s) correctamente`);

            // Notificar al padre
            if (this.props.onUploadComplete) {
              this.props.onUploadComplete();
            }
          }

          if (result.errors.length > 0) {
            ToastService.warning(`${result.errors.length} error(es) encontrado(s)`);
          }

          if (result.duplicates.length > 0) {
            ToastService.warning(`${result.duplicates.length} tarjeta(s) duplicada(s)`);
          }

        } catch (error) {
          console.error('Error uploading file:', error);
          ToastService.error(error.message || 'Error al procesar archivo');
          this.update({ uploading: false });
        }
      },

      reset() {
        this.update({
          selectedFile: null,
          result: null
        });
        const fileInput = document.querySelector('.rfid-bulk-upload input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }
      },

      downloadTemplate() {
        RfidCardService.downloadTemplate();
      },

      formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
      }
    };
  </script>

  <style>
    .rfid-bulk-upload pre {
      background-color: #f8f9fa;
      padding: 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.85em;
      max-height: 200px;
      overflow: auto;
    }

    .rfid-bulk-upload code {
      color: #d63384;
    }
  </style>
</rfid-bulk-upload>
